<template>
  <form
    class="saml-login"
    @submit.prevent
  >
    <div class="_item">
      <div
        v-if="isOkta"
        class="_label"
      >
        {{ $t('okta.email') }}
      </div>
      <div
        v-else
        class="_label"
      >
        {{ $t('general.email') }}
      </div>
      <m-text-field
        v-model:value="email"
        auto-focus
        autocomplete="username"
        :placeholder="isOkta ? $t('okta.enterEmail') : $t('loginForm.enterEmail')"
        name="username"
        input-type="text"
        @keydown.enter="submit"
      >
        <template #prefix>
          <m-icon
            type="mail"
            :color="$colors.grey.lighten2"
          />
        </template>
      </m-text-field>
    </div>
    <div v-if="displayError !== ''">
      <m-alert type="error">
        {{ displayError }}
      </m-alert>
    </div>
    <div class="_action">
      <m-btn
        color="black"
        :loading="popupLoading"
        :button-style="{ width: '100%' }"
        :style="{ width: '100%' }"
        :href="loginURL"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ $t('login.samlCallToAction') }}
      </m-btn>
    </div>
  </form>
</template>

<script>
import okta from '@/mixins/okta';
import { doSamlCandidates, getSentinelUrl } from '@/api/sentinel';
import { emailRegex } from 'shared/lib/email-regex';
import { encodeURLValues } from '@/lib/url';
import { initSamlLogin } from '@/lib/saml';
import { samlCandidatesStatus, samlLoginStatus } from '@/constants/sentinel.json';

export default {
  name: 'SamlLogin',
  props: {
    followLink: {
      type: Boolean,
      default: true,
    },
    popup: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'session-established', 'choose'],
  data() {
    return { email: '', popupError: '', popupLoading: false };
  },
  computed: {
    canSubmit() {
      return new RegExp(emailRegex()).test(this.email.trim());
    },
    loginURL() {
      if (this.popup || !this.followLink || !this.canSubmit) {
        return null;
      }
      return getSentinelUrl(`/saml/login?${encodeURLValues({ email: this.email })}`);
    },
    error() {
      if (typeof this.$route.query.idp_origin !== 'undefined') {
        return '';
      }

      if (typeof this.$route.query.saml_login_status === 'undefined') {
        return '';
      }

      return this.$t(`login.samlLoginStatus.${this.$route.query.saml_login_status}`);
    },
    displayError() {
      if (this.popupError !== '') {
        return this.popupError;
      }
      return this.error;
    },
  },
  methods: {
    submit() {
      if (!this.canSubmit) {
        return;
      }

      this.$emit('submit', this.email);

      if (this.popup) {
        this.popupSubmit();
        return;
      }

      if (this.loginURL !== null) {
        window.location.href = this.loginURL;
      }
    },
    popupSubmit() {
      this.popupError = '';
      this.popupLoading = true;
      doSamlCandidates({ email: this.email })
        .then((response) => {
          if (response?.status !== 200) {
            this.popupError = this.$t('error.default');
            return null;
          }

          const { status, candidates } = response.data;
          if (status === samlCandidatesStatus.none) {
            this.popupError = this.$t(`login.samlLoginStatus.${samlLoginStatus.unableToStart}`);
            return null;
          }
          if (status === samlCandidatesStatus.ambiguous) {
            this.$emit('choose', { email: this.email, candidates });
            return null;
          }

          return initSamlLogin({ email: this.email, slug: candidates?.[0]?.slug }).then((event) => {
            const { error } = event.data;
            if (error === samlLoginStatus.ambiguous) {
              this.popupSubmit();
              return;
            }
            if (error !== '') {
              this.popupError = samlLoginStatus.all.includes(error)
                ? this.$t(`login.samlLoginStatus.${error}`)
                : this.$t('error.default');
              return;
            }
            this.$emit('session-established');
          });
        })
        .catch(() => {})
        .finally(() => {
          this.popupLoading = false;
        });
    },
  },
  mixins: [okta],
};
</script>

<style scoped lang="scss" type="text/scss">
  .saml-login {
    ._item {
      margin-bottom: 1rem;

      ._label {
        font-size: $font-size-3;
        color: $font-color-secondary;
      }
    }
  }
</style>
